import React, { useState, useEffect, useMemo } from 'react';
import {
  MyDailyBoosterContainer,
  MyDailyBoosterTabContainerWrapper,
  MyDailyBoosterTabContent,
  MyDailyBoosterButtonsContainer,
  MyDailyBoosterTitle,
} from './MyDailyBooster.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import {
  AppContentContainer,
  HeaderContainer,
} from '../../../common/sharedStyledComponents/GenericStyles.styled';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tab } from '../../../common/sharedComponents/Tab';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';
import { IWeeklyBoosterWithViewStatusDto } from '../../../@types/boosters';
import {
  createTabsAndCardsFromContent,
  IBoosterCard,
} from './createTabsAndCards';
import { Card } from '../../../common/sharedComponents/Card';
import { CardContent, CardTitle } from '../Boosters.styled';
import { markWeeklyBoosterAsViewed } from '../../../../requests/learning';
import { MyDailyBoosterSkeleton } from './MyDailyBoosterSkeleton';
import { RichText } from '../../../common/sharedComponents/RichText';
import { BoosterNavigationButton } from '../../../common/sharedComponents/buttons/BoosterNavigationButton';
import { Body4, Headline3 } from '../../../common/typography';
import { ModalContent, ModalHeader } from '../../../common/sharedStyledComponents/Modal.styled';
import { Modal } from '../../../common/sharedComponents/Modal';

interface IMyDailyBoosterState {
  id?: string; // boosterId
  department?: string;
  booster?: IWeeklyBoosterWithViewStatusDto;
  dayNumber?: number;
  viewedCountInCycle?: number;
  totalBoostersInCycle?: number;
}

export function MyDailyBooster () {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location as { state?: IMyDailyBoosterState };
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [title, setTitle] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const boosterContent = state && state.booster && state.booster.content;
  const isViewed = state && state.booster && state.booster.isViewed;
  const isLoading = !boosterContent;

  const { tabs, cards } = useMemo(() => {
    return boosterContent
      ? createTabsAndCardsFromContent(boosterContent)
      : { tabs: [], cards: [] };
  }, [boosterContent]);

  const getCardTitle = (card: IBoosterCard) => {
    switch (card.type) {
      case 'objective':
        return t('COMMON.LEARNING.BOOSTERS.LABELS.OBJECTIVE');
      case 'advice':
        return `${t('COMMON.LEARNING.BOOSTERS.LABELS.STEP')} ${card.stepOrder}`;
      case 'feedback':
        return t('COMMON.LEARNING.BOOSTERS.LABELS.MESSAGE');
      default:
        return '';
    }
  };

  const currentCard = cards && cards[currentCardIndex];
  const currentTabIndex = currentCard ? currentCard.tabIndex : 0;
  const isLastCard = cards && currentCardIndex === cards.length - 1;

  useEffect(() => {
    if (cards && cards.length > 0 && cards[currentCardIndex]) {
      const currentCardTitle = getCardTitle(cards[currentCardIndex]);
      setTitle(currentCardTitle);
    }
  }, [currentCardIndex, cards]);

  const handleMarkAsViewed = async () => {
    // Call API to mark booster as viewed
    if (state && state.id && state.department) {
      setIsSaving(true);
      try {
        await markWeeklyBoosterAsViewed(state.id, state.department);
        // Navigate back to MyBoosters page after successful save
        navigate('/learning/myBooster', {
          state: {
            department: state.department,
          },
        });
      } catch (error) {
        console.error('Failed to mark booster as viewed:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleNext = async () => {
    // rafael review later -> add a check to see if the viewedCountInCycle is less than the totalBoostersInCycle
    if (
      isLastCard &&
      state &&
      state.viewedCountInCycle &&
      state.viewedCountInCycle + 1 === state.totalBoostersInCycle &&
      state.totalBoostersInCycle
    ) {
      setShowPopup(true);
    } else if (isLastCard) {
      handleMarkAsViewed();
    } else if (currentCardIndex < cards.length - 1) {
      const currentCardTitle = getCardTitle(cards[currentCardIndex + 1]);
      setTitle(currentCardTitle);
      setCurrentCardIndex(currentCardIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      const currentCardTitle = getCardTitle(cards[currentCardIndex - 1]);
      setTitle(currentCardTitle);
      setCurrentCardIndex(currentCardIndex - 1);
    }
  };

  const handleTabClick = (tabIndex: number) => {
    // Find the first card that belongs to this tab
    const cardIndex = cards.findIndex((card) => card.tabIndex === tabIndex);
    if (cardIndex !== -1) {
      setCurrentCardIndex(cardIndex);
    }
  };

  const handleBackNavigation = () => {
    navigate('/learning/myBooster', {
      state: {
        department: state && state.department,
      },
    });
  };

  const getActionButtonTitle = () => {
    if (isLastCard && isViewed) {
      return t('COMMON.LEARNING.BOOSTERS.BUTTONS.COMPLETED');
    }
    if (isLastCard) {
      return t('COMMON.LEARNING.BOOSTERS.BUTTONS.MARK_AS_DONE');
    }
    return t('COMMON.LEARNING.BOOSTERS.BUTTONS.NEXT');
  };
  const [showPopup, setShowPopup] = useState(false);

  return (
    <>
      {showPopup && (
        <Modal
          isOpen={showPopup}
          handleModalClose={() => setShowPopup(false)}
        >
          <ModalHeader style={{ textAlign: 'center' }}>
            <Headline3>{t('COMMON.LEARNING.BOOSTERS.MODAL.TITLE')}</Headline3>
          </ModalHeader>
          <ModalContent style={{ textAlign: 'center', marginBottom: '22px', marginTop: '22px' }}>
            <Body4>
              {t('COMMON.LEARNING.BOOSTERS.MODAL.MESSAGE')}
              <br />
              <br />
              {t('COMMON.LEARNING.BOOSTERS.MODAL.MESSAGE_2')}
            </Body4>
          </ModalContent>
          <ButtonLarge
            title={t('COMMON.LEARNING.BOOSTERS.MODAL.BUTTONS.GO_TO_NEXT_SET')}
            onClick={() => {
              setShowPopup(false);
              handleMarkAsViewed();
            }}
          />
        </Modal>
      )}
      <MyDailyBoosterContainer>
        <HeaderContainer>
          <AppHeader
            title={
              state && state.dayNumber
                ? `${t('COMMON.LEARNING.BOOSTERS.LABELS.DAY')} ${state.dayNumber} ${t('COMMON.LEARNING.BOOSTERS.LABELS.BOOSTER')}`
                : t('COMMON.LEARNING.BOOSTERS.LABELS.BOOSTER')
            }
            onClick={handleBackNavigation}
          />
        </HeaderContainer>
        <AppContentContainer>
          {isLoading && <MyDailyBoosterSkeleton />}
          {!isLoading && (
            <>
              {/* <MyDailyBoosterTitle>  */}
              {/* rafael review later -> style the headline3 */}
              <RichText
                style={{
                  marginTop: '-10px',
                  marginBottom: '18px',
                  fontSize: '20px',
                  fontWeight: '600',
                  alignSelf: 'center',
                }}
                content={(boosterContent && boosterContent.headerLine) || ''}
              />
              {/* </MyDailyBoosterTitle> */}
              <MyDailyBoosterTabContainerWrapper>
                {tabs &&
                  tabs.length > 0 &&
                  tabs.map((tab, index) => {
                    return (
                      <Tab
                        title={tab.title}
                        key={tab.title}
                        isSelected={currentTabIndex === index}
                        onClick={() => handleTabClick(index)}
                        variant="booster"
                      />
                    );
                  })}
              </MyDailyBoosterTabContainerWrapper>
              <MyDailyBoosterTabContent>
                {currentCard && (
                  <Card>
                    {title !== 'Objective' && (
                      <CardTitle>
                        {title.toLowerCase().includes('step')
                          ? t('COMMON.LEARNING.BOOSTERS.LABELS.STEP')
                          : title}
                      </CardTitle>
                    )}

                    <CardContent>
                      <RichText content={currentCard.content} />
                    </CardContent>
                  </Card>
                )}
              </MyDailyBoosterTabContent>
              <MyDailyBoosterButtonsContainer>
                {currentCardIndex > 0 && (
                  <BoosterNavigationButton
                    title={t('COMMON.LEARNING.BOOSTERS.BUTTONS.PREVIOUS')}
                    inverted={true}
                    outlined={true}
                    onClick={handlePrevious}
                    disabled={currentCardIndex === 0}
                  />
                )}
                <BoosterNavigationButton
                  title={getActionButtonTitle()}
                  onClick={handleNext}
                  loading={isSaving}
                  disabled={isLastCard && isViewed}
                  backgroundColor={isLastCard ? 'verdigris' : 'oxfordBlue'}
                />
              </MyDailyBoosterButtonsContainer>
            </>
          )}
        </AppContentContainer>
      </MyDailyBoosterContainer>
    </>
  );
}
